/**
 * CFO Cockpit → Divisions. Revenue by QBO Class (UK & Sight,
 * International, PSE, MLab, PMS) mirroring the board workbook:
 * stacked division bars, LTM by division with YoY, and the
 * quarter-vs-same-quarter-last-year revenue waterfall.
 */
import { useMemo, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Legend,
  Tooltip as RechartsTooltip, Cell, ReferenceLine,
} from "recharts";
import { fmtGbpShort, PanelTitle } from "../staff/CommissionedPanels";
import { TabState, Card, InfoTip } from "./cfoShared";
import { useQboData, QboHeader } from "./qboShared";

const COLORS = {
  "UK & Sight": "#1B4F8A",
  International: "#0D9488",
  PSE: "#d6a419",
  MLab: "#7c3aed",
  PMS: "#e11d48",
  Admin: "#a8a29e",
};

const fmtW = (v) => (v == null ? "–" : `£${Math.round(v).toLocaleString("en-GB")}`);
const fmtQ = (q) => `${q.slice(5)} ${q.slice(2, 4)}`;
const toQuarter = (ym) => `${ym.slice(0, 4)}-Q${Math.floor((+ym.slice(5, 7) - 1) / 3) + 1}`;
const fmtMon = (ym) =>
  new Date(+ym.slice(0, 4), +ym.slice(5, 7) - 1, 1).toLocaleDateString("en-GB", { month: "short", year: "2-digit" });

export default function DivisionsTab() {
  const { data, loading, error, refreshing, reload } = useQboData("story");
  const [grain, setGrain] = useState("quarter");
  const monthly = useMemo(() => data?.divisions?.monthly || [], [data]);
  const order = useMemo(() => data?.divisions?.order || [], [data]);

  const quarterly = useMemo(() => {
    const agg = new Map();
    for (const m of monthly) {
      const q = toQuarter(m.month);
      const a = agg.get(q) || { period: q, label: fmtQ(q) };
      for (const d of order) a[d] = (a[d] || 0) + (m[d] || 0);
      agg.set(q, a);
    }
    return [...agg.values()];
  }, [monthly, order]);

  const [wfQuarter, setWfQuarter] = useState(null);

  if (loading || error) {
    return <TabState loading={loading} error={error} testid="divisions-state" />;
  }

  const chart = grain === "quarter"
    ? quarterly.slice(-12)
    : monthly.slice(-18).map((m) => ({ ...m, period: m.month, label: fmtMon(m.month) }));

  // LTM by division: last 12 months vs the 12 before.
  const last12 = monthly.slice(-12);
  const prev12 = monthly.slice(-24, -12);
  const sum = (rows, d) => rows.reduce((s, r) => s + (r[d] || 0), 0);
  const ltmTotal = order.reduce((s, d) => s + sum(last12, d), 0);
  const ltmRows = order.map((d) => {
    const cur = sum(last12, d);
    const prev = sum(prev12, d);
    return {
      division: d,
      cur,
      prev,
      yoy: prev ? (100 * (cur - prev)) / Math.abs(prev) : null,
      share: ltmTotal ? (100 * cur) / ltmTotal : 0,
    };
  }).sort((a, b) => b.cur - a.cur);

  // Waterfall: chosen full quarter vs the same quarter last year.
  const fullQuarters = quarterly.filter((q) => q.period !== toQuarter(monthly[monthly.length - 1]?.month || ""));
  const wfOptions = fullQuarters.slice(-8).map((q) => q.period).reverse();
  const wfSel = wfQuarter || wfOptions[0];
  const wfLy = wfSel ? `${+wfSel.slice(0, 4) - 1}${wfSel.slice(4)}` : null;
  const qCur = quarterly.find((q) => q.period === wfSel);
  const qLy = quarterly.find((q) => q.period === wfLy);
  let waterfall = [];
  if (qCur && qLy) {
    const totLy = order.reduce((s, d) => s + (qLy[d] || 0), 0);
    const totCur = order.reduce((s, d) => s + (qCur[d] || 0), 0);
    let run = totLy;
    waterfall = [{ name: fmtQ(wfLy), base: 0, size: totLy, kind: "total", value: totLy }];
    for (const d of order) {
      const delta = (qCur[d] || 0) - (qLy[d] || 0);
      if (Math.abs(delta) < 1) continue;
      const next = run + delta;
      waterfall.push({
        name: d, base: Math.min(run, next), size: Math.abs(delta),
        kind: delta >= 0 ? "up" : "down", value: delta,
      });
      run = next;
    }
    waterfall.push({ name: fmtQ(wfSel), base: 0, size: totCur, kind: "total", value: totCur });
  }

  return (
    <div className="flex flex-col gap-6" data-testid="divisions-tab">
      <QboHeader data={data} refreshing={refreshing} onRefresh={reload} testid="divisions-refresh-btn" />

      <Card testid="divisions-chart-card">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
          <PanelTitle>Revenue by division <InfoTip id="div_revenue" /></PanelTitle>
          <div className="flex rounded-lg border border-stone-200 overflow-hidden text-[10px] font-mono">
            {[{ id: "quarter", label: "Quarterly" }, { id: "month", label: "Monthly" }].map((o) => (
              <button
                key={o.id} type="button" onClick={() => setGrain(o.id)}
                data-testid={`divisions-grain-${o.id}`}
                className={`px-2.5 py-1 transition-colors ${grain === o.id ? "bg-[#1B4F8A] text-white" : "bg-white text-stone-500 hover:bg-stone-50"}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
        <div className="h-72 mt-2">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={(v) => fmtGbpShort(v)} tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} width={56} />
              <RechartsTooltip
                contentStyle={{ fontSize: 11, borderRadius: 8 }}
                formatter={(v, n) => [fmtW(v), n]}
              />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              {order.map((d) => (
                <Bar key={d} dataKey={d} stackId="rev" fill={COLORS[d]} maxBarSize={34} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
        <p className="text-[10px] text-stone-400 mt-2">
          Billed revenue from QuickBooks, split by the Class on each invoice line. The current period is still filling.
        </p>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card testid="divisions-ltm-card">
          <PanelTitle>LTM by division · vs the 12 months before</PanelTitle>
          <table className="w-full text-xs mt-3">
            <thead>
              <tr className="text-[10px] font-mono uppercase tracking-wider text-stone-400 border-b border-stone-100">
                <th className="text-left pb-1.5">Division</th>
                <th className="text-right pb-1.5">LTM</th>
                <th className="text-right pb-1.5">Prior LTM</th>
                <th className="text-right pb-1.5">YoY</th>
                <th className="text-right pb-1.5">Share</th>
              </tr>
            </thead>
            <tbody>
              {ltmRows.map((r) => (
                <tr key={r.division} className="border-b border-stone-50" data-testid={`divisions-ltm-${r.division}`}>
                  <td className="py-1.5">
                    <span className="inline-block w-2 h-2 rounded-sm mr-2" style={{ background: COLORS[r.division] }} />
                    {r.division}
                  </td>
                  <td className="py-1.5 text-right font-mono tabular-nums font-semibold">{fmtW(r.cur)}</td>
                  <td className="py-1.5 text-right font-mono tabular-nums text-stone-500">{fmtW(r.prev)}</td>
                  <td className={`py-1.5 text-right font-mono tabular-nums ${r.yoy == null ? "text-stone-400" : r.yoy >= 0 ? "text-teal-700" : "text-rose-600"}`}>
                    {r.yoy == null ? "–" : `${r.yoy >= 0 ? "▲" : "▼"} ${Math.abs(r.yoy).toFixed(1)}%`}
                  </td>
                  <td className="py-1.5 text-right font-mono tabular-nums text-stone-500">{r.share.toFixed(0)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <Card testid="divisions-waterfall-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <PanelTitle>Quarter waterfall · vs same quarter last year <InfoTip id="div_waterfall" /></PanelTitle>
            <select
              value={wfSel || ""}
              onChange={(e) => setWfQuarter(e.target.value)}
              data-testid="divisions-waterfall-select"
              className="text-[11px] font-mono border border-stone-200 rounded-lg px-2 py-1 bg-white text-slate-700"
            >
              {wfOptions.map((q) => (
                <option key={q} value={q}>{fmtQ(q)} vs {fmtQ(`${+q.slice(0, 4) - 1}${q.slice(4)}`)}</option>
              ))}
            </select>
          </div>
          <div className="h-64 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={waterfall} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 9, fill: "#a8a29e" }} axisLine={false} tickLine={false} interval={0} />
                <YAxis tickFormatter={(v) => fmtGbpShort(v)} tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} width={56} />
                <RechartsTooltip
                  contentStyle={{ fontSize: 11, borderRadius: 8 }}
                  formatter={(v, n, p) => (n === "size" ? [fmtW(p?.payload?.value), p?.payload?.kind === "total" ? "revenue" : "change"] : null)}
                />
                <ReferenceLine y={0} stroke="#e7e5e4" />
                <Bar dataKey="base" stackId="wf" fill="transparent" isAnimationActive={false} />
                <Bar dataKey="size" stackId="wf" maxBarSize={44}>
                  {waterfall.map((w, i) => (
                    <Cell
                      key={i}
                      fill={w.kind === "total" ? "#1B4F8A" : w.kind === "up" ? "#0D9488" : "#e11d48"}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="text-[10px] text-stone-400 mt-2">
            How each division moved the quarter against the same quarter last year. Teal grew, red shrank.
          </p>
        </Card>
      </div>
    </div>
  );
}
